<template>
  <component is="div">
    <b-modal
      id="modal-comp-trans"
      ok-title="Modify"
      centered
      size="xl"
      :title="`Update an existing transcript`"
      ref="modal"
      @hidden="() => closeEdit()"
      :visible="isEditOpen"
      @ok="() => submitTranscriptEdit()"
      :ok-disabled="false"
      :no-close-on-backdrop="true"
    >
      <b-row class="w-full" :key="id" v-for="{ _id: id, program, courses } in transcriptEdit.pathways">
        <b-row class="w-100 mb-2">
          <b-col cols="2">
            <b-form-group label="Name" :label-for="`program-${id}-name`">
              <span class="align-text-top text-capitalize">
                <b-form-input :id="`pathway-${id}-name`" v-model="program.name" />
              </span>
            </b-form-group>
          </b-col>
          <b-col cols="1">
            <b-form-group label="Credential" :label-for="`program-${id}-type`">
              <span class="align-text-top text-capitalize">
                <v-select
                  v-model="program.credential"
                  :options="credentialOptions"
                  :id="`program-${id}-type`"
                  :reduce="(val) => val.value"
                  :clearable="false"
                />
              </span>
            </b-form-group>
          </b-col>
          <b-col cols="1">
            <b-form-group label="Hours" :label-for="`program-${id}-hours`">
              <b-input-group>
                <cleave
                  :id="`program-${id}-hours`"
                  v-model="program.hours"
                  class="form-control"
                  :raw="true"
                  :options="options.hours"
                  placeholder="0"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="1">
            <b-form-group label="Transfer Hours" :label-for="`program-${id}-transfer_hours`">
              <b-input-group>
                <cleave
                  :id="`program-${id}-transfer_hours`"
                  v-model="program.transferHours"
                  class="form-control"
                  :raw="true"
                  :options="options.hours"
                  placeholder="0"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="1">
            <b-form-group label="GPA" :label-for="`program-${id}-gpa`">
              <b-input-group>
                <cleave
                  :id="`program-${id}-gpa`"
                  v-model="program.gpa"
                  class="form-control"
                  :raw="true"
                  :options="options.gpa"
                  placeholder="0"
                />
              </b-input-group>
            </b-form-group>
          </b-col>

          <!--  Dates-->
          <b-col cols="2">
            <b-form-group label="Enrollment Date" :label-for="`program-${id}-date_enrollment`">
              <b-input-group>
                <flat-pickr
                  :id="`program-${id}-date_enrollment`"
                  class="form-control"
                  :config="{ altInput: true, altFormat: 'F j, Y', dateFormat: 'Z' }"
                  v-model="program.enrollmentDate"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="2">
            <b-form-group label="Begin Date" :label-for="`program-${id}-date_begin`">
              <b-input-group>
                <flat-pickr
                  :id="`program-${id}-date_begin`"
                  class="form-control"
                  :config="{ altInput: true, altFormat: 'F j, Y', dateFormat: 'Z' }"
                  v-model="program.beginDate"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="2">
            <b-form-group label="Completion Date" :label-for="`program-${id}-date_completion`">
              <b-input-group>
                <flat-pickr
                  :id="`program-${id}-date_completion`"
                  class="form-control"
                  :config="{ altInput: true, altFormat: 'F j, Y', dateFormat: 'Z' }"
                  v-model="program.completionDate"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="w-100">
          <b-table
            :items="courses"
            sort-by="date"
            :fields="[
              { key: 'course', label: 'Course' },

              { key: 'startDate', label: 'Begin' },
              { key: 'endDate', label: 'Completed' },

              { key: 'clockHours', label: 'Hours' },
              { key: 'grade', label: 'Grade' },
              { key: 'gradePoints', label: 'GPA' },
              { key: 'retake', label: 'Retake' },
              { key: 'transferHours', label: 'Transfer Hours' },
              { key: 'qualityPoints', label: 'Quality Points' },
            ]"
          >
            <template #cell(course)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">
                  <b-form-input v-model="data.item.course.name" />
                </span>
              </div>
            </template>
            <template #cell(startDate)="data">
              <div class="text-nowrap">
                <p v-if="!data.item.startDate">(inherited)</p>
                <span v-else class="align-text-top text-capitalize">
                  <flat-pickr
                    class="w-100 form-control"
                    :config="{ altInput: true, altFormat: 'F j, Y', dateFormat: 'Z' }"
                    v-model="data.item.startDate"
                  />
                </span>
              </div>
            </template>
            <template #cell(endDate)="data">
              <div class="text-nowrap">
                <p v-if="!data.item.startDate">(inherited)</p>
                <span v-else class="align-text-top text-capitalize">
                  <flat-pickr
                    class="w-100 form-control"
                    :config="{ altInput: true, altFormat: 'F j, Y', dateFormat: 'Z' }"
                    v-model="data.item.endDate"
                  />
                </span>
              </div>
            </template>

            <template #cell(clockHours)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">
                  <cleave v-model="data.item.clockHours" class="form-control" :raw="true" :options="options.hours" placeholder="0" />
                </span>
              </div>
            </template>

            <template #cell(transferHours)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">
                  <cleave v-model="data.item.transferHours" class="form-control" :raw="true" :options="options.hours" placeholder="0" />
                </span>
              </div>
            </template>

            <template #cell(gradePoints)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">
                  <cleave v-model="data.item.gradePoints" class="form-control" :raw="true" :options="options.gpa" placeholder="0" />
                </span>
              </div>
            </template>

            <template #cell(grade)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">
                  <b-form-input v-model="data.item.grade" />
                </span>
              </div>
            </template>
            <template #cell(qualityPoints)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">
                  <cleave v-model="data.item.qualityPoints" class="form-control" :raw="true" :options="options.hours" placeholder="0" />
                </span>
              </div>
            </template>
            <template #cell(retake)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">
                  <v-select
                    v-model="data.item.retake"
                    :options="[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]"
                    :reduce="(val) => val.value"
                    :clearable="false"
                  />
                </span>
              </div>
            </template>
          </b-table>
        </b-row>
      </b-row>
    </b-modal>
  </component>
</template>

<script>
import { BCol, BFormDatepicker, BFormGroup, BFormInput, BInputGroup, BRow, BTable } from 'bootstrap-vue';
import 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import store from '@/store/index';
import ToastificationContent from '@core/components/toastification/ToastificationContent';
import { useToast } from 'vue-toastification/composition';
import moment from 'moment';
import { avatarText, title } from '@core/utils/filter';
import usePathwayCohorts from '@/views/apps/roster/pathways/cohorts/usePathwayCohorts';
import useCourseSections from '@/views/apps/roster/courses/sections/useCourseSections';
import vSelect from 'vue-select';
import Cleave from 'vue-cleave-component';
import { heightTransition } from '@core/mixins/ui/transition';
import Ripple from 'vue-ripple-directive';
import flatPickr from 'vue-flatpickr-component';

export default {
  components: {
    Cleave,
    BCol,
    BRow,
    BTable,
    BFormGroup,
    BFormInput,
    BFormDatepicker,
    BInputGroup,
    vSelect,
    flatPickr,
  },
  directives: {
    Ripple,
  },
  mixins: [heightTransition],
  data() {
    return {
      transcriptEdit: {
        pathways: [],
        status: 'active',
      },
      // The transcript actually loaded, which differs from `transcriptId` when the
      // PDF's own transcript was replaced (DEV-747).
      editTranscriptId: null,

      options: {
        hours: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralDecimalScale: 0,
        },
        gpa: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralDecimalScale: 2,
        },
        score: {
          numeral: true,
          numeralPositiveOnly: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralDecimalScale: 2,
        },
      },
    };
  },
  props: {
    isEditOpen: {
      type: Boolean,
    },
    transcriptId: {
      type: String,
    },
  },
  emits: ['refresh', 'update:is-edit-open'],
  mounted() {
    store
      .dispatch('complianceStoreModule/getComplianceTranscript', {
        transcriptId: this.transcriptId,
        options: { populate: 'pathways.pathway pathways.course pathways.courses.course', resolveReplaced: true },
      })
      .then((response) => {
        if (response.status === 200) {
          const { data } = response;

          this.transcriptEdit = data;
          this.editTranscriptId = data.id;
          this.initTrHeight();

          if (data.replacedTranscriptId) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Editing the current transcript',
                icon: 'InfoIcon',
                variant: 'warning',
                text: 'The transcript for this PDF was replaced, so the current transcript for this student was opened instead.',
              },
            });
          }
        }
      })
      .catch((error) => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to fetch transcript',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response ? error.response.data.message : error.message,
          },
        });
      });
  },
  created() {
    window.addEventListener('resize', this.initTrHeight);
  },
  destroyed() {
    window.removeEventListener('resize', this.initTrHeight);
  },
  methods: {
    initTrHeight() {
      this.trSetHeight(null);
      this.$nextTick(() => {
        this.trSetHeight(this.$refs.form.scrollHeight);
      });
    },
    closeEdit() {
      this.transcriptEdit.isModalOpen = false;
      this.$emit('update:is-edit-open', false);
      this.$emit('refresh');
    },
    submitTranscriptEdit() {
      const pathways = this.transcriptEdit.pathways.map((pathway) => {
        const courses = pathway.courses.map(({ id: _id, ...course }) => {
          return {
            _id,
            ...course,
            course: course.course.id,
          };
        });

        return {
          ...pathway,
          course: pathway.course ? pathway.course.id : undefined,
          pathway: pathway.pathway ? pathway.pathway.id : undefined,
          courses,
        };
      });

      const payload = { pathways };

      store
        .dispatch('complianceStoreModule/updateComplianceTranscript', {
          transcriptId: this.editTranscriptId || this.transcriptId,
          transcriptData: payload,
        })
        .then((response) => {
          if (response.status === 200) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Compliance transcript has been modified!',
                icon: 'EditIcon',
                variant: 'success',
              },
            });
          }
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to edit transcript',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error.response ? error.response.data.message : error.message,
            },
          });
        })
        .finally(() => this.$nextTick(() => this.$emit('refresh')));
    },
  },
  computed: {
    totalResults() {
      return this.transcriptEdit.pathways.length;
    },
  },
  setup(props) {
    const { resolvePathwayCohortName, resolvePathwayCohortStatusVariant, resolvePathwayCohortVisibilityVariant } = usePathwayCohorts();

    const { resolveCourseSectionName } = useCourseSections();

    const toast = useToast();

    const credentialOptions = [
      { label: 'Certificate', value: 'certificate' },
      { label: 'Diploma', value: 'diploma' },
      { label: 'N/A', value: 'na' },
    ];

    return {
      toast,
      title,
      moment,
      avatarText,
      resolvePathwayCohortName,
      resolveCourseSectionName,
      resolvePathwayCohortStatusVariant,
      resolvePathwayCohortVisibilityVariant,
      credentialOptions,
    };
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
